import type { ReminderSettings, Subscription, User } from '@/core/domain';
import { userHasActivePlanAccess, userRequiresSubscriptionRenewal } from '@/core/domain';
import { ROUTES, appSectionPath, parseAppSectionFromPathname } from '@/app/routes';
import { APP_SECTION } from '@/features/app/constants/sections';
import type { AppSectionId } from '@/features/app/constants/sections';

/** Precisa ir para escolha de plano / checkout (sem plano ativo ou renovação obrigatória). */
export function subscriptionRequiresPlanChoice(subscription: Subscription | null): boolean {
  return (
    subscription?.plan_id == null ||
    !userHasActivePlanAccess(subscription) ||
    userRequiresSubscriptionRenewal(subscription)
  );
}

/**
 * Rota inicial após sessão resolvida (documento `users`, assinatura e teste de lembrete).
 * Ordem: onboarding → planos → configurações (teste WhatsApp) → app início.
 */
export function resolvePostLoginAppPath(params: {
  domainUser: User | null;
  subscription: Subscription | null;
  messageTestSended: boolean;
}): string {
  if (!params.domainUser) return ROUTES.AUTH_ONBOARDING;
  if (subscriptionRequiresPlanChoice(params.subscription)) return ROUTES.CHOOSE_PLAN;
  if (!params.messageTestSended) return appSectionPath(APP_SECTION.TESTAR_ENVIO);
  return appSectionPath(APP_SECTION.DASHBOARD);
}

/**
 * Carrega usuário, assinatura e configurações de lembrete e devolve a rota inicial da sessão.
 */
export async function loadSessionHomePath(params: {
  uid: string;
  getUserById: (id: string, options?: { forceRefresh?: boolean }) => Promise<User | null>;
  getSubscriptionByUserId: (userId?: string) => Promise<Subscription | null>;
  getReminderSettingsByUserId: () => Promise<ReminderSettings | null>;
}): Promise<string> {
  const domainUser = await params.getUserById(params.uid, { forceRefresh: true });
  if (!domainUser) return ROUTES.AUTH_ONBOARDING;
  const subscription = await params.getSubscriptionByUserId(params.uid);
  if (subscriptionRequiresPlanChoice(subscription)) return ROUTES.CHOOSE_PLAN;
  const settings = await params.getReminderSettingsByUserId();
  const messageTestSended = settings?.message_test_sended === true;
  return resolvePostLoginAppPath({ domainUser, subscription, messageTestSended });
}

/**
 * Secções que continuam acessíveis com o gate ativo (teste de envio pendente):
 * fluxo de configuração, conta, planos. O restante do app é desbloqueado no sidebar;
 * aqui só evitamos ficar em rotas “de trabalho” sem concluir o teste.
 */
const APP_SHELL_REMINDER_GATE_EXEMPT_SECTIONS: readonly AppSectionId[] = [
  APP_SECTION.TESTAR_ENVIO,
  APP_SECTION.MINHA_CONTA,
  APP_SECTION.PLANOS,
];

/**
 * Dentro do shell `/app/*`: se assinatura ok mas o teste de envio ainda não foi feito,
 * redireciona para configurações de lembretes, exceto nas secções isentas (conta, planos, etc.).
 */
export function resolveAppShellReminderGateRedirect(
  pathname: string,
  messageTestSended: boolean
): string | null {
  if (messageTestSended) return null;
  const section: AppSectionId | null = parseAppSectionFromPathname(pathname);
  if (section != null && APP_SHELL_REMINDER_GATE_EXEMPT_SECTIONS.includes(section)) return null;
  return appSectionPath(APP_SECTION.TESTAR_ENVIO);
}
